import { apiFetch } from "./api.js";
import { trackEvent } from "../lib/analytics.js";
/** @typedef {import('@qm/types').ChatMessage} ChatMessage */

/**
 * Send a chat message (lean payload — backend fetches history from DB).
 * EMAIL-11: Identity derived from JWT on the server. Phone only needed for admin-preview.
 * RWD-8: the response field is `xpEarned` (was `cpEarned` pre-RWD-8). The
 * backend rewards engine now writes XP directly — no more CP tier.
 * @param {string} personaId - The persona to chat with
 * @param {string} phone - Voter phone number (only used for admin-preview, otherwise JWT handles identity)
 * @param {string} message - The message text
 * @param {"positive"|"neutral"|"negative"} [sentiment] - Client-computed sentiment of the message
 * @param {boolean} [isSuggested] - Whether the message was a suggested/recommended question (no XP earned)
 * @returns {Promise<{ reply: string, xpEarned: number, cached: boolean, userMessageId: string, assistantMessageId: string, isDuplicate: boolean, kbReferenceCount: number, isSuggested: boolean }>}
 */
export async function sendMessage(personaId, phone, message, sentiment, isSuggested) {
  const result = await apiFetch(`/api/chat/${personaId}`, {
    method: "POST",
    body: JSON.stringify({
      // Only send phone for admin-preview bypass; JWT handles normal identity
      ...(phone ? { phone } : {}),
      message,
      type: "standard",
      sentiment,
      ...(isSuggested ? { isSuggested: true } : {}),
    }),
  });
  trackEvent("chat_message", { persona_id: personaId, cached: result.cached ? "true" : "false", xp_earned: result.xpEarned ?? 0 });
  return result;
}

/**
 * Request a deep-dive follow-up on an AI response (lean payload).
 * Returns { reply: string, sources: [{ title, url }] }
 */
export async function sendDeepDive(personaId, phone, context) {
  return apiFetch(`/api/chat/${personaId}`, {
    method: "POST",
    body: JSON.stringify({
      ...(phone ? { phone } : {}),
      type: "deep_dive",
      context,
    }),
  });
}

/**
 * Get chat history for a voter+persona pair.
 * EMAIL-11: Server derives identity from JWT. Phone param only for admin-preview.
 * Returns { messages, total } with messages in chronological order.
 */
export async function getChatHistory(personaId, phone, limit = 10, offset = 0) {
  // Only include phone param for admin-preview; JWT handles normal identity
  const params = phone ? `phone=${encodeURIComponent(phone)}&` : "";
  return apiFetch(`/api/chat/${personaId}?${params}limit=${limit}&offset=${offset}`);
}

/**
 * Bulk-import messages from localStorage migration.
 */
export async function importChatHistory(personaId, phone, messages) {
  return apiFetch(`/api/chat/${personaId}/import`, {
    method: "POST",
    body: JSON.stringify({
      ...(phone ? { phone } : {}),
      messages,
    }),
  });
}

/**
 * CHAT-2: Submit feedback (like/dislike/flag) for a chat message.
 * RWD-8: the response field is `xpEarned` (was `cpEarned` pre-RWD-8).
 * Returns { ok: true, xpEarned: number }
 */
export async function submitFeedback(messageId, feedback, flagReason, phone, personaId) {
  return apiFetch(`/api/chat/messages/${messageId}/feedback`, {
    method: "POST",
    body: JSON.stringify({ feedback, flagReason, phone, personaId }),
  });
}

/**
 * CHAT-1: Get dynamic suggested prompts for a persona+voter.
 * EMAIL-11: Server derives identity from JWT. Phone param only for admin-preview.
 * Returns { suggestions: string[] }
 */
export async function getSuggestions(personaId, phone) {
  const params = phone ? `phone=${encodeURIComponent(phone)}` : "";
  return apiFetch(`/api/chat/${personaId}/suggestions?${params}`);
}
